class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('#MiniCart');
    this.header = document.querySelector('sticky-header');
    this.recommendations = new Map();
    this.onViewportChange = this.position.bind(this);

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('focusin', this.onFocusIn.bind(this));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh();
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'mini-cart' && event.source !== 'product-form') this.refresh();
    });
  }

  get isOpen() {
    return this.panel.classList.contains('is-open');
  }

  get icon() {
    return document.getElementById('cart-icon-bubble');
  }

  onDocumentClick(event) {
    const icon = event.target.closest('#cart-icon-bubble');
    if (icon) {
      // The cart page already shows the cart, and modified clicks should still open /cart.
      if (window.location.pathname === routes.cart_url || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.setActiveElement(icon);
        this.open();
      }
      return;
    }

    if (!this.isOpen) return;
    if (!this.contains(event.target)) return this.close(false);
    if (event.target.closest('[data-mini-cart-close]')) return this.close();

    const control = event.target.closest('[data-mini-cart-quantity]');
    if (control) this.updateQuantity(control.dataset.miniCartLine, Number(control.dataset.miniCartQuantity));

    const add = event.target.closest('[data-mini-cart-add]');
    if (add) this.addItem(Number(add.dataset.miniCartAdd));

    const upgrade = event.target.closest('[data-mini-cart-upgrade]');
    if (upgrade) {
      const { miniCartLine, miniCartUpgrade, miniCartUpgradeQuantity } = upgrade.dataset;
      this.swapItem(miniCartLine, miniCartUpgrade, Number(miniCartUpgradeQuantity));
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  // Closes when keyboard focus moves on to the rest of the page.
  onFocusIn(event) {
    if (this.isOpen && !this.contains(event.target)) this.close(false);
  }

  open() {
    this.position();
    this.panel.classList.add('is-open');
    this.icon.setAttribute('aria-expanded', true);
    this.panel.focus({ preventScroll: true });
    this.loadRecommendations();
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
  }

  close(returnFocus = true) {
    this.panel.classList.remove('is-open');
    this.icon.setAttribute('aria-expanded', false);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
    if (returnFocus && this.activeElement) this.activeElement.focus({ preventScroll: true });
  }

  // The panel sits outside the header, so it lines up with the icon as the sticky header resizes.
  position() {
    const icon = this.icon.getBoundingClientRect();
    this.panel.style.setProperty('--mini-cart-top', `${Math.max(Math.round(icon.bottom + 8), 0)}px`);
    this.panel.style.setProperty('--mini-cart-right', `${Math.max(Math.round(window.innerWidth - icon.right), 16)}px`);
  }

  updateQuantity(key, quantity) {
    this.postCart(routes.cart_change_url, { id: key, quantity });
  }

  addItem(variantId, quantity = 1) {
    this.postCart(`${routes.cart_add_url}.js`, { items: [{ id: variantId, quantity }] });
  }

  // One request, so the cart is never left with both sizes or neither.
  swapItem(key, variantId, quantity) {
    this.postCart(`${routes.cart_update_url}.js`, { updates: { [key]: 0, [variantId]: quantity } });
  }

  postCart(url, payload) {
    if (this.classList.contains('is-loading')) return;
    this.classList.add('is-loading');

    const body = JSON.stringify({
      ...payload,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(url, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((state) => {
        if (state.errors || state.status) {
          return this.showError(state.errors || state.description || window.cartStrings.error);
        }
        this.renderSections(state.sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: state });
      })
      .catch(() => this.showError(window.cartStrings.error))
      .finally(() => this.classList.remove('is-loading'));
  }

  refresh() {
    const sections = this.getSectionsToRender().map((section) => section.id);
    fetch(`${window.location.pathname}?sections=${sections.join(',')}`)
      .then((response) => response.json())
      .then((state) => this.renderSections(state))
      .catch((error) => console.error(error));
  }

  // Based on the newest cart line. Anything already in the cart is left out, and a failure just shows no row.
  loadRecommendations() {
    const container = this.querySelector('#MiniCart-recommendations');
    if (!container || !container.hidden) return;

    const { productId, cartProducts } = container.dataset;
    if (!this.recommendations.has(productId)) {
      const url = `${this.dataset.recommendationsUrl}?section_id=mini-cart-recommendations&product_id=${productId}&limit=6&intent=related`;
      this.recommendations.set(
        productId,
        fetch(url).then((response) => (response.ok ? response.text() : Promise.reject()))
      );
    }

    this.recommendations
      .get(productId)
      .then((html) => {
        if (!container.isConnected) return;
        const source = new DOMParser().parseFromString(html, 'text/html');
        const inCart = cartProducts.split(',');
        const items = [...source.querySelectorAll('.mini-cart__recommendation')];
        items.filter((item) => inCart.includes(item.dataset.productId)).forEach((item) => item.remove());
        items.filter((item) => item.isConnected).slice(3).forEach((item) => item.remove());
        if (!source.querySelector('.mini-cart__recommendation')) return;
        container.innerHTML = source.getElementById('MiniCartRecommendations').innerHTML;
        container.hidden = false;
      })
      .catch(() => {});
  }

  showError(message) {
    const error = this.querySelector('.mini-cart__error');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  renderSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      if (!sections[section.id]) return;
      const source = new DOMParser().parseFromString(sections[section.id], 'text/html').querySelector(section.selector);
      if (source) document.getElementById(section.target).innerHTML = source.innerHTML;
    });
    if (this.isOpen) {
      this.panel.focus({ preventScroll: true });
      this.loadRecommendations();
    }
  }

  // These four members are the interface product-form.js expects from a cart element.
  renderContents(parsedState) {
    this.renderSections(parsedState.sections);
    if (this.header && this.header.reveal) this.header.reveal();
    if (!this.isOpen) this.open();
  }

  getSectionsToRender() {
    return [
      { id: 'mini-cart', target: 'MiniCart-contents', selector: '#MiniCart-contents' },
      { id: 'cart-icon-bubble', target: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);
